import { lazy } from "react";

type PanelModules = typeof import("./panelModules");

let panelPromise: Promise<PanelModules> | null = null;

/**
 * Load (once) the lazy panel chunk. Safe to call repeatedly — every
 * caller shares the same promise, so idle prefetch, launcher hover,
 * and the actual render never fetch twice. A failed load is forgotten
 * so the next intent (or React's retry) can try again.
 */
export function loadPanel(): Promise<PanelModules> {
  if (!panelPromise) {
    panelPromise = import("./panelModules").catch((err: unknown) => {
      panelPromise = null;
      throw err;
    });
  }
  return panelPromise;
}

/** Fire-and-forget prefetch for launcher hover/focus/press. */
export function prefetchPanel(): void {
  void loadPanel().catch(() => {
    // Swallowed here only: the render path calls ``loadPanel`` again
    // and surfaces a real failure through Suspense/error handling.
  });
}

const IDLE_FALLBACK_MS = 1500;

/**
 * Prefetch the panel once the host page has settled, so a visitor
 * who clicks the launcher rarely waits on the network. Returns a
 * cancel function for effect cleanup.
 */
export function prefetchPanelWhenIdle(): () => void {
  if (typeof window === "undefined") return () => {};
  const idleWindow = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (handle: number) => void;
  };
  if (idleWindow.requestIdleCallback) {
    const handle = idleWindow.requestIdleCallback(prefetchPanel, {
      timeout: IDLE_FALLBACK_MS * 2,
    });
    return () => idleWindow.cancelIdleCallback?.(handle);
  }
  const timer = window.setTimeout(prefetchPanel, IDLE_FALLBACK_MS);
  return () => window.clearTimeout(timer);
}

export const LazyWidgetTranscript = lazy(() =>
  loadPanel().then((m) => ({ default: m.WidgetTranscript })),
);
export const LazyVideoPlayer = lazy(() =>
  loadPanel().then((m) => ({ default: m.VideoPlayer })),
);
export const LazyScripturePanel = lazy(() =>
  loadPanel().then((m) => ({ default: m.ScripturePanel })),
);
